import { useId, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ListBulletIcon,
  ReaderIcon,
  ReloadIcon,
  TargetIcon,
} from '@radix-ui/react-icons';
import {
  EBOOK_TIME_LIMIT_SECONDS,
  type EbookLearningProgress,
  type EbookQuestion,
  type EbookQuestionProgress,
  type EbookWorkspace,
} from '@wx-weiqi/contracts';
import {
  learningQuestionIds,
  learningResult,
  reviewLabel,
  type EbookLearningFilter,
} from '../services/ebook-learning';
import {
  ebookChapterNames,
  ebookChapterQuestionIds,
  normalizeEbookChapter,
} from '../services/ebook-chapters';
import { BoardSurface } from './BoardSurface';
import './ebook-practice-workspace.css';

export type EbookPracticeWorkspaceProps = {
  book: EbookWorkspace['book'];
  questions: EbookQuestion[];
  progress: EbookLearningProgress | null;
  chapter: string | null;
  onChapter: (chapter: string | null) => void;
  filter: EbookLearningFilter;
  visibleIds: string[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onFilter: (filter: EbookLearningFilter) => void;
  onStart: (question: EbookQuestion) => void;
  onBack: () => void;
  busy: boolean;
  error: string;
  preview: boolean;
  continueId: string | null;
  onContinue: () => void;
  onRetry: () => void;
  initialTab?: 'chapters' | 'questions';
};

function questionNumber(book: EbookWorkspace['book'], question: EbookQuestion) {
  const sequential = book.questionIds.indexOf(question.id) + 1;
  return book.numberStyle === 'SOURCE'
    ? question.number.trim() || String(sequential)
    : String(sequential).padStart(2, '0');
}

function LearningState({
  progress,
  loaded,
  preview,
}: {
  progress: EbookQuestionProgress | undefined;
  loaded: boolean;
  preview: boolean;
}) {
  const state = preview
    ? 'preview'
    : !loaded
      ? 'unknown'
      : progress?.pending?.submitted === false
        ? 'pending'
        : progress?.reviewState === 'DUE'
          ? 'review'
          : progress?.completedCount
            ? 'completed'
            : 'unseen';
  return (
    <span className={`ebook-practice-state ebook-practice-state--${state}`}>
      {preview
        ? '预览'
        : !loaded
          ? '记录未载入'
          : progress?.pending?.submitted === false
            ? '作答中'
            : progress
              ? reviewLabel(progress)
              : '未完成'}
    </span>
  );
}

function FirstPlayer({ color }: { color: EbookQuestion['currentPlayer'] }) {
  return (
    <span className="ebook-practice-first-player">
      <img
        src={`/assets/teacher-lecture/${color === 'WHITE' ? 'white-shell' : 'black-slate'}.png`}
        width="30"
        height="30"
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      <span>{color === 'WHITE' ? '白先' : '黑先'}</span>
    </span>
  );
}

export function EbookPracticeWorkspace({
  book,
  questions,
  progress,
  chapter,
  onChapter,
  filter,
  visibleIds,
  selectedId,
  onSelect,
  onFilter,
  onStart,
  onBack,
  busy,
  error,
  preview,
  continueId,
  onContinue,
  onRetry,
  initialTab = 'chapters',
}: EbookPracticeWorkspaceProps) {
  const [chaptersOpen, setChaptersOpen] = useState(initialTab === 'chapters');
  const directoryId = useId();
  const chaptersId = useId();
  const progressLoaded = progress !== null;
  const questionById = new Map(questions.map((question) => [question.id, question]));
  const progressById = new Map(progress?.questions.map((entry) => [entry.questionId, entry]));
  const visible = new Set(visibleIds);
  const selected = selectedId ? questionById.get(selectedId) : undefined;
  const selectedProgress = selected ? progressById.get(selected.id) : undefined;
  const selectedHasPendingAttempt = !preview && selectedProgress?.pending?.submitted === false;
  const chapterNames = ebookChapterNames(book, questions);
  const chapterIds = ebookChapterQuestionIds(book, questions, chapter);
  const chapterLabel = chapter === null ? '全部章节' : chapter || '未分章';
  const incompleteCount = progressLoaded
    ? learningQuestionIds(book.questionIds, progress.questions, 'UNSEEN').length
    : null;
  const reviewCount = progressLoaded
    ? learningQuestionIds(book.questionIds, progress.questions, 'REVIEW').length
    : null;
  const chapterIncompleteCount = progressLoaded
    ? learningQuestionIds(chapterIds, progress.questions, 'UNSEEN').length
    : null;
  const chapterReviewCount = progressLoaded
    ? learningQuestionIds(chapterIds, progress.questions, 'REVIEW').length
    : null;
  const missingQuestions = book.questionIds.some((id) => !questionById.has(id));
  const hasVisibleQuestions = book.questionIds.some((id) => visible.has(id));
  const continuation = continueId ? questionById.get(continueId) : undefined;
  const hasPendingAttempt =
    progress?.resume?.submitted === false && progress.resume.questionId === continueId;
  const canShowContinuation = !preview && progressLoaded && continueId !== null;
  const continuationCrossesChapter = continuation && !chapterIds.includes(continuation.id);
  const unavailableProgress = !preview && !progressLoaded;
  const emptyChapter = chapter !== null && chapterIds.length === 0;
  const emptyMessage =
    busy && !hasVisibleQuestions
      ? '正在载入题目…'
      : chapterIds.length === 0
        ? chapter === null
          ? '这本电子书还没有题目。'
          : chapter === ''
            ? '所有题目都已归入章节，可以选择其他章节练习。'
            : '本章还没有题目，可以选择其他章节练习。'
        : unavailableProgress && filter !== 'ALL'
          ? '学习记录尚未载入，暂时无法筛选。'
          : preview && filter !== 'ALL'
            ? '预览不记录学习进度，请查看全部题目。'
            : filter === 'REVIEW'
              ? `${chapter === null ? '本书' : '本章'}暂时没有需要复习的题目。已安排的复习，到时间会出现在这里。`
              : filter === 'UNSEEN'
                ? `${chapter === null ? '本书' : '本章'}的题目都练习过了，可以查看全部题目或到“待复习”巩固一下。`
                : '当前没有可查看的题目。';
  const selectChapter = (nextChapter: string | null) => {
    onChapter(nextChapter);
    setChaptersOpen(false);
  };

  const showDirectoryTab = () => setChaptersOpen(true);
  const showAllQuestionsTab = () => setChaptersOpen(false);

  return (
    <section className="ebook-practice-workspace" aria-label="电子书练习工作台">
      <header className="ebook-practice-banner">
        <div className="ebook-practice-banner-art" aria-hidden="true">
          <img
            src="/assets/ebook-cover/practice-banner-scene-v2.webp"
            width="2172"
            height="724"
            alt=""
            decoding="async"
            draggable={false}
          />
        </div>
        <div className="ebook-practice-banner-copy">
          <button className="ebook-practice-back" type="button" onClick={onBack}>
            <ArrowLeftIcon aria-hidden="true" /> 返回
          </button>
          <h1>{book.title}</h1>
          <p className="ebook-practice-summary">
            <span>共 {book.questionIds.length} 题</span>
            {preview ? (
              <span>学生视角预览 · 不计学习成绩</span>
            ) : progressLoaded ? (
              <>
                <span>已完成练习 {book.questionIds.length - (incompleteCount ?? 0)} 题</span>
                <span>待复习 {reviewCount} 题</span>
              </>
            ) : (
              <span>{busy ? '正在读取学习记录…' : '学习记录暂未载入'}</span>
            )}
          </p>
        </div>
      </header>

      {(error || (unavailableProgress && !busy)) && (
        <div className="ebook-practice-alert" role="alert">
          <p>{error || '学习记录未能载入，请重试后查看练习进度。'}</p>
          <button type="button" disabled={busy} onClick={onRetry}>
            <ReloadIcon aria-hidden="true" /> {busy ? '正在重试…' : '重新加载'}
          </button>
        </div>
      )}

      <div className="ebook-practice-layout">
        <aside className="ebook-practice-directory" aria-label="题目目录">
          <div className="ebook-practice-directory-heading">
            <h2>
              <ReaderIcon aria-hidden="true" /> 题目目录
            </h2>
          </div>

          <div className="ebook-practice-directory-tabs" role="tablist" aria-label="题目目录视图">
            <button
              type="button"
              role="tab"
              aria-selected={chaptersOpen}
              aria-controls={chaptersId}
              disabled={busy}
              onClick={showDirectoryTab}
            >
              章节目录
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!chaptersOpen}
              aria-controls={directoryId}
              disabled={busy}
              onClick={showAllQuestionsTab}
            >
              所有题目
            </button>
          </div>

          <div className="ebook-practice-directory-content" id={directoryId}>
            <section className="ebook-practice-chapters" id={chaptersId} hidden={!chaptersOpen}>
              <div className="ebook-practice-chapters-heading">
                <h3>
                  <ListBulletIcon aria-hidden="true" />
                  选择章节
                </h3>
                <span>共 {chapterNames.length} 章</span>
              </div>
              <ol className="ebook-practice-chapter-list" aria-label="选择练习章节">
                {[...chapterNames, ''].map((name, index) => {
                  const ids = ebookChapterQuestionIds(book, questions, name);
                  const label = name || '未分章';
                  const completed = progressLoaded
                    ? ids.filter((id) => (progressById.get(id)?.completedCount ?? 0) > 0).length
                    : 0;
                  const completion = ids.length ? Math.round((completed / ids.length) * 100) : 0;
                  const tone = index === 0 ? 'teal' : index === 1 ? 'clay' : 'neutral';
                  return (
                    <li key={`chapter:${name || 'unassigned'}`}>
                      <button
                        type="button"
                        aria-pressed={chapter === name}
                        disabled={busy}
                        onClick={() => selectChapter(name)}
                      >
                        <span className="ebook-practice-chapter-main">
                          <span
                            className="ebook-practice-chapter-marker"
                            data-tone={tone}
                            aria-hidden="true"
                          />
                          <span className="ebook-practice-chapter-name">{label}</span>
                        </span>
                        <span className="ebook-practice-chapter-progress">
                          <small>{ids.length} 题</small>
                          <span
                            className="ebook-practice-chapter-progress-track"
                            role="progressbar"
                            aria-label={`${label}已完成 ${completed} 题`}
                            aria-valuemin={0}
                            aria-valuemax={ids.length}
                            aria-valuenow={completed}
                          >
                            <span style={{ width: `${completion}%` }} />
                          </span>
                        </span>
                        <ArrowRightIcon aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ol>
              {chapterNames.length === 0 && (
                <p className="ebook-practice-chapters-note">本书尚未分章，题目都在“未分章”中。</p>
              )}
            </section>

            {!chaptersOpen && (
              <>
                <div className="ebook-practice-chapter-scope" aria-label="当前章节">
                  <h3>{chapterLabel}</h3>
                  {chapter !== null && (
                    <button type="button" disabled={busy} onClick={() => selectChapter(null)}>
                      全部章节
                    </button>
                  )}
                </div>
                <div className="ebook-practice-filters" role="group" aria-label="筛选练习题">
                  {(
                    [
                      ['ALL', '全部', chapterIds.length],
                      ['UNSEEN', '未完成', preview ? null : chapterIncompleteCount],
                      ['REVIEW', '待复习', preview ? null : chapterReviewCount],
                    ] as const
                  ).map(([value, label, count]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={filter === value}
                      disabled={busy || (value !== 'ALL' && (preview || !progressLoaded))}
                      onClick={() => onFilter(value)}
                      title={preview && value !== 'ALL' ? '预览不记录学习进度' : undefined}
                    >
                      {label} <span>{count ?? '—'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {canShowContinuation && (
              <button
                className="ebook-practice-continue"
                type="button"
                disabled={busy}
                onClick={onContinue}
              >
                <ReaderIcon className="ebook-practice-continue-icon" aria-hidden="true" />
                <span>
                  {hasPendingAttempt ? '继续上次练习' : '练习下一题'}
                  <small>
                    {continuation
                      ? `第 ${questionNumber(book, continuation)} 题`
                      : hasPendingAttempt
                        ? '此前版本的未完成题目'
                        : '继续学习'}
                  </small>
                  {continuationCrossesChapter && (
                    <small>
                      {normalizeEbookChapter(continuation.chapter) || '未分章'} · 继续后切换章节
                    </small>
                  )}
                </span>
                <ArrowRightIcon aria-hidden="true" />
              </button>
            )}

            {!chaptersOpen &&
              (hasVisibleQuestions ? (
                <ol className="ebook-practice-question-list" aria-label="选择预览题目">
                  {book.questionIds.map((id, index) => {
                    if (!visible.has(id)) return null;
                    const question = questionById.get(id);
                    if (!question)
                      return (
                        <li className="ebook-practice-missing-question" key={id}>
                          第 {index + 1} 题 <span>内容未载入</span>
                        </li>
                      );
                    return (
                      <li key={id}>
                        <button
                          className="ebook-practice-question"
                          type="button"
                          aria-pressed={selected?.id === id}
                          disabled={busy}
                          onClick={() => {
                            onSelect(id);
                          }}
                        >
                          <span className="ebook-practice-question-number">
                            第 {questionNumber(book, question)} 题
                          </span>
                          <FirstPlayer color={question.currentPlayer} />
                          <LearningState
                            progress={progressById.get(id)}
                            loaded={progressLoaded}
                            preview={preview}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="ebook-practice-empty" role="status">
                  <p>{emptyMessage}</p>
                  {emptyChapter ? (
                    <button type="button" disabled={busy} onClick={() => selectChapter(null)}>
                      查看全部章节
                    </button>
                  ) : (
                    filter !== 'ALL' && (
                      <button type="button" disabled={busy} onClick={() => onFilter('ALL')}>
                        查看全部题目
                      </button>
                    )
                  )}
                </div>
              ))}
            {missingQuestions && (
              <div className="ebook-practice-directory-note" role="status">
                <p>部分题目内容未载入，重新加载后即可查看。</p>
                <button type="button" disabled={busy} onClick={onRetry}>
                  重新加载题目
                </button>
              </div>
            )}
          </div>
        </aside>

        <div className="ebook-practice-preview">
          {selected ? (
            <>
              <figure
                className="ebook-practice-board"
                aria-label={`第 ${questionNumber(book, selected)} 题棋形预览`}
              >
                <BoardSurface
                  boardAppearance="wood"
                  size={selected.size}
                  stones={selected.stones}
                  lastMove={null}
                  initialAnnotations={selected.marks.map((mark) =>
                    mark.kind === 'LABEL'
                      ? { point: mark.point, kind: 'LABEL' as const, text: mark.label }
                      : { point: mark.point, kind: mark.kind },
                  )}
                />
                <figcaption className="ebook-practice-visually-hidden">
                  题目初始棋形。点击开始练习后作答。
                </figcaption>
              </figure>
              <aside className="ebook-practice-details" aria-label="题目预览">
                <header className="ebook-practice-details-heading">
                  <h2>
                    <TargetIcon aria-hidden="true" /> 题目预览
                  </h2>
                  <p className="ebook-practice-selected-number">
                    第 {questionNumber(book, selected)} 题
                  </p>
                  {selected.title && (
                    <p className="ebook-practice-question-title">{selected.title}</p>
                  )}
                  <LearningState
                    progress={selectedProgress}
                    loaded={progressLoaded}
                    preview={preview}
                  />
                </header>

                <section className="ebook-practice-requirements" aria-label="题目要求">
                  <h3>题目要求</h3>
                  <FirstPlayer color={selected.currentPlayer} />
                  {selected.goal.trim() &&
                    selected.goal.trim() !==
                      (selected.currentPlayer === 'WHITE' ? '白先' : '黑先') && (
                      <p>{selected.goal}</p>
                    )}
                  {selected.text.trim() && selected.text.trim() !== selected.goal.trim() && (
                    <p>{selected.text}</p>
                  )}
                </section>

                <section className="ebook-practice-record" aria-label="本题作答记录">
                  <h3>本题作答记录</h3>
                  {preview ? (
                    <p className="ebook-practice-record-note">预览不计成绩，也不记录复习进度。</p>
                  ) : progressLoaded ? (
                    <>
                      <dl className="ebook-practice-record-counts">
                        <div className="ebook-practice-correct">
                          <dt>答对</dt>
                          <dd>
                            <strong>{selectedProgress?.correct ?? 0}</strong> <span>次</span>
                          </dd>
                        </div>
                        <div className="ebook-practice-incorrect">
                          <dt>答错</dt>
                          <dd>
                            <strong>{selectedProgress?.incorrect ?? 0}</strong> <span>次</span>
                          </dd>
                        </div>
                      </dl>
                      {selectedProgress && (
                        <details className="ebook-practice-record-history">
                          <summary>查看练习记录</summary>
                          <p>首次：{learningResult(selectedProgress.first)}</p>
                          <p>最近：{learningResult(selectedProgress.latest)}</p>
                          {selectedProgress.reviewCount > 0 && (
                            <p>已复习 {selectedProgress.reviewCount} 次</p>
                          )}
                          {selectedProgress.pending?.submitted === false &&
                            selectedProgress.reviewState !== 'NONE' && (
                              <p>{reviewLabel(selectedProgress)}</p>
                            )}
                        </details>
                      )}
                    </>
                  ) : (
                    <p className="ebook-practice-record-note" role="status">
                      {busy ? '正在读取作答记录…' : '作答记录暂未载入，请重新加载。'}
                    </p>
                  )}
                </section>

                <div className="ebook-practice-start-area">
                  <button
                    className="ebook-practice-start"
                    type="button"
                    disabled={busy}
                    onClick={() => onStart(selected)}
                  >
                    {busy
                      ? '正在加载…'
                      : preview
                        ? '开始预览练习'
                        : selectedHasPendingAttempt
                          ? '继续练习'
                          : '开始练习'}
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                  <p>
                    {selectedHasPendingAttempt
                      ? '继续原作答 · 计时不会重新开始'
                      : `开始后计时 · 限时${EBOOK_TIME_LIMIT_SECONDS}秒`}
                  </p>
                </div>
              </aside>
            </>
          ) : (
            <div className="ebook-practice-selection-empty" role="status">
              <ReaderIcon aria-hidden="true" />
              <h2>{hasVisibleQuestions ? '选择一道题，开始练习' : '当前没有可预览的题目'}</h2>
              <p>
                {hasVisibleQuestions
                  ? '在题目目录选择题目，查看棋形与要求后再开始作答。'
                  : emptyMessage}
              </p>
              {emptyChapter ? (
                <button type="button" disabled={busy} onClick={() => selectChapter(null)}>
                  查看全部章节
                </button>
              ) : (
                filter !== 'ALL' && (
                  <button type="button" disabled={busy} onClick={() => onFilter('ALL')}>
                    查看全部题目
                  </button>
                )
              )}
              {filter === 'ALL' && !emptyChapter && !busy && !hasVisibleQuestions && (
                <button
                  type="button"
                  onClick={chapter === null ? onBack : () => selectChapter(null)}
                >
                  {chapter === null ? '返回书架' : '查看全部章节'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
